import { Pipe, PipeTransform, inject } from '@angular/core';
import { LocalizationDisplayService } from 'src/core/services/settings/localization-display.service';

/**
 * Renders the date part of a timestamp with the tenant's Date Format preference
 * (Administration &gt; Settings &gt; Localization): `06/08/2026`, `08/06/2026` or `2026-08-06`.
 *
 * Use `displayDateTime` when the clock is needed too.
 *
 * The optional `timezone` is a passthrough for values that must not drift across zones, such as a
 * birth date stored at UTC midnight. It is not the tenant time zone preference.
 *
 * ```html
 * {{ call.startedAt | displayDate }}
 * {{ contact.birthDate | displayDate: 'UTC' }}
 * ```
 */
@Pipe({
  name: 'displayDate',
  standalone: true,
  pure: false,
})
export class DisplayDatePipe implements PipeTransform {
  private readonly localizationDisplay = inject(LocalizationDisplayService);

  private cachedInput: Date | string | number | null | undefined;
  private cachedTimezone: string | undefined;
  private cachedSettingsHash = '';
  private cachedResult: string | null = null;

  transform(
    value: Date | string | number | null | undefined,
    timezone?: string
  ): string | null {
    const settingsHash = this.localizationDisplay.settingsHash;

    if (
      value === this.cachedInput &&
      timezone === this.cachedTimezone &&
      settingsHash === this.cachedSettingsHash
    ) {
      return this.cachedResult;
    }

    this.cachedInput = value;
    this.cachedTimezone = timezone;
    this.cachedSettingsHash = settingsHash;
    this.cachedResult = this.localizationDisplay.formatDate(value, timezone);

    return this.cachedResult;
  }
}
